import { NotifyErrorWrapper, NotifySuccess } from '@/helpers/core/notifications'
import {
  createInboundShipment,
  createOutboundShipment,
  deleteShipment,
  getAllShipments,
  getShipmentContainers,
  getShipmentDetails,
  updateInboundShipment,
  updateOutboundShipment,
} from '@/services/rc/shipments.service'
import type { IShipmentFilters } from '@/types/rc/filters'
import type { IShipmentFormInbound, IShipmentFormOutbound } from '@/types/rc/forms'
import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query'
import { computed, type ComputedRef, type Reactive, type Ref } from 'vue'

export const useShipments = () => {
  const queryClient = useQueryClient()

  // Core queries

  const getShipmentsQuery = (filters: Reactive<IShipmentFilters>) =>
    useQuery({
      queryKey: ['shipments', filters],
      queryFn: () => getAllShipments(filters),
      placeholderData: (prev) => prev,
    })

  const getShipmentDetailsQuery = (shipmentId: Ref<number | undefined>, queryActive?: Ref<boolean>) =>
    useQuery({
      queryKey: ['shipment-details', shipmentId],
      queryFn: () => getShipmentDetails(shipmentId.value!),
      enabled: () => !!shipmentId.value && (queryActive?.value ?? true),
    })

  // Core mutations

  const createInboundShipmentMutation = useMutation({
    mutationFn: (inboundShipmentForm: IShipmentFormInbound) => createInboundShipment(inboundShipmentForm),
    onSuccess: async (data, form) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['shipments'] }),
        queryClient.invalidateQueries({ queryKey: ['work-order-logistics', form.work_order?.number] }),
      ])
      NotifySuccess(
        `Created inbound shipment for ${form.work_order!.customer ?? "customer's"} Work Order #${form.work_order!.number.slice(-4)}`
      )
    },
  })

  const updateInboundShipmentMutation = useMutation({
    mutationFn: (inboundShipmentForm: IShipmentFormInbound) => updateInboundShipment(inboundShipmentForm),
    onSuccess: async (data, form) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['shipments'] }),
        queryClient.invalidateQueries({ queryKey: ['shipment-details', form.id] }),
        queryClient.invalidateQueries({ queryKey: ['work-order-logistics', form.work_order?.number] }),
      ])
      NotifySuccess(
        `Updated inbound shipment for ${form.work_order!.customer ?? "customer's"} Work Order #${form.work_order!.number!.slice(-4)}`
      )
    },
  })

  const createOutboundShipmentMutation = useMutation({
    mutationFn: (outboundShipmentForm: IShipmentFormOutbound) => createOutboundShipment(outboundShipmentForm),
    onSuccess: async (data, form) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['shipments'] }),
        queryClient.invalidateQueries({ queryKey: ['work-order-logistics', form.work_order?.number] }),
      ])
      NotifySuccess(
        `Created outbound shipment for ${form.work_order!.customer ?? "customer's"} Work Order #${form.work_order!.number!.slice(-4)}`
      )
    },
  })

  const updateOutboundShipmentMutation = useMutation({
    mutationFn: (outboundShipmentForm: IShipmentFormOutbound) => updateOutboundShipment(outboundShipmentForm),
    onSuccess: async (data, form) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['shipments'] }),
        queryClient.invalidateQueries({ queryKey: ['shipment-details', form.id] }),
        queryClient.invalidateQueries({ queryKey: ['work-order-logistics', form.work_order?.number] }),
      ])
      NotifySuccess(
        `Updated outbound shipment for ${form.work_order!.customer ?? "customer's"} Work Order #${form.work_order!.number!.slice(-4)}`
      )
    },
  })

  const deleteShipmentMutation = useMutation({
    mutationFn: deleteShipment,
    onSuccess: () => {
      NotifySuccess('Shipment deleted successfully.')
      queryClient.invalidateQueries({ queryKey: ['shipments'] })
    },
    onError: NotifyErrorWrapper('Could not delete shipment'),
  })

  // Container-related

  const scanningViewShipmentContainersQuery = (shipmentId: ComputedRef<number> | Ref<number>) =>
    useQuery({
      queryKey: ['scanning-shipment-containers', shipmentId],
      queryFn: () => getShipmentContainers(shipmentId.value, { page: 1, per_page: 100 }),
      refetchOnWindowFocus: false,
    })

  // Convenience values and functions
  // Involve states and functions used on ShipmentDialog, omit others that aren't (as deleteShipmentMutation)

  const isAnyMutationPending = computed(
    () =>
      createInboundShipmentMutation.isPending.value ||
      updateInboundShipmentMutation.isPending.value ||
      createOutboundShipmentMutation.isPending.value ||
      updateOutboundShipmentMutation.isPending.value
  )

  const isAnyMutationSucceeded = computed(
    () =>
      createInboundShipmentMutation.isSuccess.value ||
      updateInboundShipmentMutation.isSuccess.value ||
      createOutboundShipmentMutation.isSuccess.value ||
      updateOutboundShipmentMutation.isSuccess.value
  )

  const resetAllMutations = () => {
    createInboundShipmentMutation.reset()
    updateInboundShipmentMutation.reset()
    createOutboundShipmentMutation.reset()
    updateOutboundShipmentMutation.reset()
  }

  return {
    getShipmentsQuery,
    getShipmentDetailsQuery,
    createInboundShipmentMutation,
    updateInboundShipmentMutation,
    createOutboundShipmentMutation,
    updateOutboundShipmentMutation,
    deleteShipmentMutation,
    scanningViewShipmentContainersQuery,
    isAnyMutationPending,
    isAnyMutationSucceeded,
    resetAllMutations,
  }
}
